import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react';
import { MEDIA_QUERIES, useMediaQuery } from '@utils/hooks/use-media-query.tsx';
import { PrivateRoute } from '../../../router/restricted-routes-wrapper.tsx';
import cn from 'classnames';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { css, StyleSheet } from 'aphrodite';
import PageErrorBoundary from '@components/layout/page-error-boundary.tsx';
import Logo from '@components/shared/logo.tsx';
import { Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AppShellProps {
  /** Desktop sidebar component */
  sidebar: ReactNode;
  /** Render prop that receives open state — returns the mobile drawer */
  mobileSidebar: (
    open: boolean,
    setOpen: Dispatch<SetStateAction<boolean>>
  ) => ReactNode;
  /** Page content */
  children: ReactNode;
  /** Banners rendered above everything (maintenance, upload-suspended, acting-as) */
  banners?: ReactNode;
  /** Persistent top bar rendered above main content on desktop (#2021). */
  topBar?: ReactNode;
  layoutType?: 'main' | 'editor';
  disableOverflowY?: boolean;
}

export default function AppShell({
  sidebar,
  mobileSidebar,
  children,
  banners,
  topBar,
  layoutType,
  disableOverflowY
}: AppShellProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [largeTabletAndAbove] = useMediaQuery(
    `(min-width: ${MEDIA_QUERIES.LARGE_TABLET})`
  );

  const styles = useMemo(
    () =>
      StyleSheet.create({
        main: {
          backgroundColor: getColour('baseLayer'),
          color: getColour('normalText')
        },
        mobileBar: {
          backgroundColor: getColour('secondLayer'),
          borderBottom: `1px solid ${getColour('border')}`
        }
      }),
    [getColour]
  );

  const isEditor = layoutType === 'editor';

  return (
    <PrivateRoute>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[10000] focus:px-4 focus:py-2 focus:rounded-2xl focus:shadow-pop focus:bg-app focus:text-primary focus:outline-2 focus:outline-primary"
      >
        {t('skip-to-content')}
      </a>
      {banners && (
        <div role="region" aria-label={t('announcements')}>
          {banners}
        </div>
      )}

      <div className={cn('w-full h-screen flex flex-col', css(styles.main))}>
        {/* Mobile top bar */}
        {!largeTabletAndAbove && (
          <header
            className={cn(
              'h-14 flex-shrink-0 flex items-center justify-between px-4',
              css(styles.mobileBar)
            )}
          >
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="inline-flex items-center justify-center h-9 w-9 rounded-2xl"
              style={{ color: getColour('icon') }}
              aria-label={t('open-sidebar')}
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
            <Logo isLink />
            <div className="w-9" aria-hidden="true" />
          </header>
        )}

        <div className="flex flex-row flex-1 min-h-0">
          {largeTabletAndAbove
            ? sidebar
            : mobileSidebar(sidebarOpen, setSidebarOpen)}

          <div className="flex flex-col flex-1 min-w-0">
            {/* Editor pages own their chrome (transcript/caption "about"
                header). Suppress the persistent app top bar so the sidebar
                rail + editor header form a single consistent layout. */}
            {!isEditor && topBar && (
              <header className="flex-shrink-0">{topBar}</header>
            )}
            <main
              id="main-content"
              className={cn(
                'flex-1 min-w-0 flex flex-col',
                isEditor
                  ? 'overflow-y-auto'
                  : cn(
                      // ``min-h-0`` alongside ``flex-1``: flex children
                      // default to ``min-height: auto`` (never shrink
                      // below content), so tall children pushed <main>
                      // to grow instead of scrolling. With ``min-h-0``,
                      // <main> honours its allotted flex slice;
                      // ``overflow-y-auto`` then actually kicks in when
                      // content overflows.
                      'lg:px-4 lg:py-2 sm:px-6 sm:py-4 gap-y-3 min-h-0',
                      !disableOverflowY && 'overflow-y-auto',
                      css(styles.main)
                    )
              )}
            >
              <PageErrorBoundary>{children}</PageErrorBoundary>
            </main>
          </div>
        </div>
      </div>
    </PrivateRoute>
  );
}
